/** Shared kid-color palette + record type.
 *
 *  Extracted from TodayTab.tsx so that KidsCrewSetup and MyKidsSection can
 *  import it without a static dependency on the (lazy-loaded) TodayTab
 *  chunk. TodayTab imports from here too — same values, one module.
 */

/** Stable color palette used by addKnownKid / Settings UI when assigning a
 *  permanent color for a newly-known kid. Colors persist in
 *  user_preferences.known_kid_colors (jsonb) so a kid keeps the same color
 *  even when other kids are added/removed. Order intentionally differs from
 *  CHILD_PALETTE (in TodayTab) so the first-known kid lands on a clear,
 *  recognizable green. */
export const KID_COLOR_PALETTE = [
  "#43AA8B", // Green
  "#F4A261", // Coral
  "#5B9BD5", // Blue
  "#B47ED8", // Purple
  "#E76F51", // Terracotta
  "#F9C74F", // Yellow
  "#577590", // Slate
  "#90BE6D", // Lime
] as const;

/** Map of canonical kid name -> hex color (case-insensitive lookups via
 *  helpers in TodayTab). Persisted as known_kid_colors jsonb. */
export type KidColorRecord = Record<string, string>;

/** Case-insensitive color lookup. Mirrors lookupPersistedKidColor in TodayTab
 *  and findColor in MyKidsSection (same semantics, one shared implementation
 *  for newer callers). */
export function findKidColor(
  name: string,
  colors: KidColorRecord | null | undefined
): string | null {
  if (!name || !colors) return null;
  if (colors[name]) return colors[name];
  const lc = name.toLowerCase();
  for (const k of Object.keys(colors)) {
    if (k.toLowerCase() === lc) return colors[k];
  }
  return null;
}

/** First color in the stable palette that isn't already assigned. Matches
 *  pickAvailableColor in MyKidsSection so a kid added from any surface gets
 *  the same color it would have gotten from Settings. */
export function pickAvailableKidColor(existing: KidColorRecord): string {
  const used = new Set(Object.values(existing));
  for (const c of KID_COLOR_PALETTE) {
    if (!used.has(c)) return c;
  }
  return KID_COLOR_PALETTE[Object.keys(existing).length % KID_COLOR_PALETTE.length];
}
